import { For, Show } from "solid-js";
import { createStore } from "solid-js/store";
import { Portal } from "solid-js/web";
import { TransitionGroup } from "@fleet/ui";
import { AlertCircle, CheckCircle2, Info, X } from "@fleet/icons";

import { useSwipeToDismiss } from "@/lib/gestures/use-swipe-to-dismiss";

type ToastType = "success" | "error" | "info" | "warning";

/** Optional toast action (e.g. "Undo" from ACTIVITY-CORE L1). */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  action?: ToastAction;
}

const TOAST_DURATION = 5000;
const RADIX_36 = 36;
const ID_LENGTH = 9;
const [toasts, setToasts] = createStore<Toast[]>([]);

function removeToast(id: string) {
  setToasts(toasts.filter((t) => t.id !== id));
}

export function showToast(
  message: string,
  type: ToastType = "info",
  options?: { action?: ToastAction },
) {
  const id = Math.random().toString(RADIX_36).slice(2, ID_LENGTH);
  setToasts([...toasts, { id, message, type, action: options?.action }]);

  setTimeout(() => {
    removeToast(id);
  }, TOAST_DURATION);
}

function ToastItem(props: { toast: Toast }) {
  let surfaceRef: HTMLDivElement | undefined;

  const swipe = useSwipeToDismiss({
    getSurfaceRef: () => surfaceRef,
    onDismiss: () => {
      removeToast(props.toast.id);
    },
  });

  return (
    <div
      ref={surfaceRef}
      // Error toasts must interrupt assistive tech immediately (CC-30): an API
      // failure surfaced here is the only feedback the user gets, so it uses the
      // assertive `alert` live region. Non-errors stay polite `status`.
      role={props.toast.type === "error" ? "alert" : "status"}
      aria-live={props.toast.type === "error" ? "assertive" : "polite"}
      // pan-y: keep native vertical scroll, hand the horizontal axis to the swipe.
      class="flex touch-pan-y select-none items-center justify-between gap-3 rounded-lg border bg-popover p-4 shadow-lg"
      classList={{
        "border-success/30 text-success": props.toast.type === "success",
        "border-destructive/30 text-destructive": props.toast.type === "error",
        "border-primary/30 text-primary": props.toast.type === "info",
        "border-warning/30 text-warning": props.toast.type === "warning",
        "transition-[transform,opacity] duration-200 ease-out":
          swipe.isAnimating() && !swipe.isSwiping(),
      }}
      style={
        swipe.isSwiping() || swipe.isAnimating() || swipe.tx() !== 0
          ? {
              transform: `translateX(${swipe.tx()}px)`,
              opacity: swipe.opacity(),
            }
          : {}
      }
      {...swipe.handlers}
    >
      <div class="flex min-w-0 items-center gap-3">
        <Show when={props.toast.type === "success"}>
          <CheckCircle2 class="h-5 w-5 shrink-0" />
        </Show>
        <Show when={props.toast.type === "error"}>
          <AlertCircle class="h-5 w-5 shrink-0" />
        </Show>
        <Show when={props.toast.type === "warning"}>
          <AlertCircle class="h-5 w-5 shrink-0" />
        </Show>
        <Show when={props.toast.type === "info"}>
          <Info class="h-5 w-5 shrink-0" />
        </Show>
        <span class="min-w-0 break-words text-sm font-medium">{props.toast.message}</span>
      </div>
      <div class="flex shrink-0 items-center gap-1">
        <Show when={props.toast.action}>
          {(action) => (
            <button
              type="button"
              data-testid="toast-action"
              onClick={() => {
                action().onClick();
                removeToast(props.toast.id);
              }}
              class="rounded-md px-2 py-1 text-xs font-semibold underline-offset-2 hover:underline"
            >
              {action().label}
            </button>
          )}
        </Show>
        <button
          type="button"
          onClick={() => {
            removeToast(props.toast.id);
          }}
          class="rounded-full p-1 transition-colors hover:bg-black/5"
        >
          <X class="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export function Toaster() {
  return (
    <Portal>
      <div
        class="pointer-events-none fixed inset-x-0 bottom-0 z-[300] flex flex-col gap-2 sm:inset-x-auto sm:right-0 sm:w-full sm:max-w-[400px]"
        style={{
          // Full-width with margins on mobile, honouring the notch/home-bar
          // safe-area insets so toasts never tuck under system UI or overflow.
          "padding-left": "calc(0.75rem + var(--safe-area-inset-left))",
          "padding-right": "calc(0.75rem + var(--safe-area-inset-right))",
          "padding-bottom": "calc(0.75rem + var(--safe-area-inset-bottom))",
        }}
      >
        <TransitionGroup name="toast">
          <For each={toasts}>
            {(toast) => (
              <div class="pointer-events-auto">
                <ToastItem toast={toast} />
              </div>
            )}
          </For>
        </TransitionGroup>
      </div>
    </Portal>
  );
}
